import { Button } from '@/components/ui/button';
import {
  useGetListPromotions,
  useMutateApplyPromotion,
  useMutateValidateVoucher,
} from '@/hooks/api/storefront/promotion';
import { ChevronLeft } from 'lucide-react';
import { Dispatch, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { StoreCartDetail } from '@/types/http';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  cn,
  generatePromotionsPayload,
  isEmpty,
  mergePromotions,
  parseErrorMessage,
} from '@/lib/utils';
import { AxiosError } from 'axios';
import { BasePromotion } from '@/types/http/promotion/common';
import ModalAlert from '@/components/sections/modal-alert';
import useModal from '@/hooks/useModal';
import { useParams } from 'next/navigation';
import { toast } from '@/components/ui/use-toast';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { queryClient } from '@/lib/query-client';
import { BaseFreeGift, usePromotionStore } from '@/stores/promotion';
import ModalGiftReward from '@/components/sections/builder/elements/body/cart-total/component/modal-gift-reward';
import { shallow } from 'zustand/shallow';
import { ThemeColorSchemeType, ThemePromotionType } from '@/stores/builder';
import CardPromotion from '@/components/sections/builder/elements/body/checkout-form/component/card-promotion';
import { buildAutoFreeGifts, detectConflictPromotion, ensureApplied } from '@/lib/promotions';
import DynamicImage from '@/components/commons/dynamic-image';

type VoucherListProps = {
  carts: StoreCartDetail;
  sectionView: 'detail' | 'voucher' | 'active_offers';
  setSectionView: Dispatch<React.SetStateAction<'detail' | 'voucher' | 'active_offers'>>;
  colorScheme: ThemeColorSchemeType;
  promotionTheme?: ThemePromotionType;
};

export default function VoucherList({
  carts,
  sectionView,
  setSectionView,
  colorScheme,
  promotionTheme,
}: VoucherListProps) {
  const token = Cookies.get('accessToken') as string;
  const params = useParams();
  const checkoutId = params.checkoutId as string;
  const modalUseVoucher = useModal();
  const modalGift = useModal();

  const [voucherCode, setVoucherCode] = useState<string>('');
  const [errorVoucher, setErrorVoucher] = useState<string | null>(null);
  const [redeemedVouchers, setRedeemedVouchers] = useState<BasePromotion[]>([]);
  const [pendingReplacePromos, setPendingReplacePromos] = useState<BasePromotion[] | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<BasePromotion | null>(null);
  const [giftPromotion, setGiftPromotion] = useState<BasePromotion | null>(null);
  const [maxQtyReward, setMaxQtyReward] = useState<number>(0);

  const [selectedTab, setSelectedTab] = useState<'product_group' | 'product_variant'>(
    'product_group'
  );

  const {
    selectedPromotion,
    selectedFreeGift,
    setSelectedPromotion,
    replaceFreeGift,
    removeFreeGift,
    applyVoucherRewards,
    syncFreeGiftPromotion,
  } = usePromotionStore(
    (s) => ({
      selectedPromotion: s.selectedPromotion,
      selectedFreeGift: s.selectedFreeGift,
      setSelectedPromotion: s.setSelectedPromotion,
      removeFreeGift: s.removeFreeGift,
      replaceFreeGift: s.replaceFreeGift,
      applyVoucherRewards: s.applyVoucherRewards,
      syncFreeGiftPromotion: s.syncFreeGiftPromotion,
    }),
    shallow
  );

  /**
   * Payload for list promotions
   * @param items - Array of cart items
   * @returns - Object containing items and grand total
   */
  const payloadListPromotion = useMemo(() => {
    const items = (carts?.items || []).filter((item) => !item.promotion_id);

    const { mappedItems, grandTotal } = items.reduce(
      (acc, item) => {
        const discountAmount = item.discount_amount ?? 0;
        const sellPrice = item.sell_price;

        const pricePerItem = sellPrice;
        const itemTotal = item.quantity * pricePerItem;

        acc.mappedItems.push({
          item_id: item.id,
          qty: item.quantity,
          amount: itemTotal,
        });

        acc.grandTotal += itemTotal - discountAmount;

        return acc;
      },
      {
        mappedItems: [] as {
          item_id: string;
          qty: number;
          amount: number;
        }[],
        grandTotal: 0,
      }
    );

    return {
      items: mappedItems,
      grand_total: grandTotal,
      current_date: new Date().toISOString(),
    };
  }, [carts?.items]);

  /**
   * Fetch list of promotions
   * @param payloadListPromotion - Payload for list promotions
   * @param token - Access token
   * @returns - List of promotions
   */
  const { data: listPromotions, isLoading: isLoadingPromotions } = useGetListPromotions(
    payloadListPromotion,
    false,
    token as string,
    {
      enabled: !!payloadListPromotion.items.length && sectionView === 'voucher',
    }
  );

  const allPromotions = useMemo(() => {
    const merged: BasePromotion[] = [];
    listPromotions?.forEach((promo) => merged.push(promo));
    selectedPromotion?.forEach((promo) => {
      if (!merged.some((p) => p.promotion_id === promo.promotion_id)) merged.push(promo);
    });
    redeemedVouchers.forEach((promo) => {
      if (!merged.some((p) => p.promotion_id === promo.promotion_id)) merged.push(promo);
    });

    // Sort: use_voucher: true di atas
    merged.sort((a, b) => {
      const aVoucher = !!a.use_voucher;
      const bVoucher = !!b.use_voucher;
      if (aVoucher === bVoucher) return 0;
      return aVoucher ? -1 : 1; // use_voucher: true duluan
    });

    return merged;
  }, [listPromotions, selectedPromotion, redeemedVouchers]);

  /**
   * Check if promotion is disabled
   * @param promo - Promotion
   * @returns - Boolean
   */
  const isPromoDisabled = (promo: BasePromotion): boolean => {
    const isAlreadySelected = selectedPromotion.some((p) => p.promotion_id === promo.promotion_id);
    if (isAlreadySelected) return false;

    const hasNonStackableSelected = selectedPromotion.some((p) => p.is_overlap === false);
    const hasStackableSelected = selectedPromotion.some((p) => p.is_overlap === true);
    const isEligible = (payloadListPromotion.grand_total || 0) >= promo.minimum_spending;

    if (!isEligible) return true;
    if (hasNonStackableSelected) return true;
    if (hasStackableSelected && promo.is_overlap === false) return true;

    return false;
  };

  /**
   * Validate voucher
   * @param payload - Payload for validating voucher
   * @param token - Access token
   * @returns - Void
   */
  const validateVoucher = useMutateValidateVoucher({
    onSuccess: (data) => {
      if (!data.data.is_valid) return;

      const promoRewards = data.data.promotion_rewards;
      const voucherCode = data.data.voucher_code;

      const rewardsWithCode = promoRewards.map((p) => ({ ...p, voucher_code: voucherCode }));

      const isNewOverlapFalse = rewardsWithCode.some((p) => p.is_overlap === false);
      const conflictPromo = detectConflictPromotion(selectedPromotion ?? [], rewardsWithCode);

      if (conflictPromo) {
        setPendingReplacePromos(promoRewards);
        modalUseVoucher.open();
        return;
      }

      const choiceGiftPromos = rewardsWithCode.filter(isChoiceGiftPromo);
      const singleGiftPromos = rewardsWithCode.filter(isFixedSingleGift);
      const plainPromos = rewardsWithCode.filter(
        (p) => !isFixedSingleGift(p) && !isChoiceGiftPromo(p)
      );

      const immediatePromos = [...plainPromos, ...singleGiftPromos];

      if (immediatePromos.length > 0) {
        applyVoucherRewards(rewardsWithCode, voucherCode, isNewOverlapFalse);

        if (singleGiftPromos.length > 0) {
          const { replaceFreeGift, removeFreeGift, syncFreeGiftPromotion } =
            usePromotionStore.getState();
          const nextSelected = usePromotionStore.getState().selectedPromotion;
          const nextIds = new Set(nextSelected.map((p) => p.promotion_id));

          const sources = [...rewardsWithCode, ...(listPromotions ?? [])];

          for (const sp of singleGiftPromos) {
            if (!nextIds.has(sp.promotion_id)) continue; // tidak aktif → skip
            const enriched = ensureApplied(sp, sources);
            const gifts = buildAutoFreeGifts(enriched);
            if (gifts.length > 0) replaceFreeGift(sp.promotion_id, gifts, 'replace');
            else removeFreeGift(sp.promotion_id);
          }
          syncFreeGiftPromotion();
        }
      }

      if (choiceGiftPromos.length > 0) {
        const p0 = choiceGiftPromos[0];
        setPendingPromotion(p0);
        setGiftPromotion(p0);
        setMaxQtyReward(
          p0.counted_multiply
            ? (p0.product_reward?.[0]?.qty ?? 0) * p0.counted_multiply
            : p0.product_reward?.[0]?.qty ?? 0
        );
        modalGift.setIsShow(true);
        return;
      } else {
        setVoucherCode('');
        toast({
          variant: 'success',
          title: 'Success',
          description: 'Voucher redeemed successfully',
        });
      }
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        setErrorVoucher(error.response?.data?.message);
      }
    },
  });

  /**
   * Handle redeem voucher
   * @param voucherCode - Voucher code to redeem
   * @returns - Void
   */
  const handleRedeemVoucher = async () => {
    if (!voucherCode.trim()) {
      setErrorVoucher('Please enter a voucher code');
      return;
    }

    if (selectedPromotion.some((promo) => promo.voucher_code === voucherCode)) {
      setErrorVoucher('Voucher code already used');
      return;
    }

    const payload = {
      voucher_code: voucherCode,
      order: {
        items: payloadListPromotion?.items?.map((item) => ({
          item_id: item.item_id,
          qty: item.qty,
        })),
        grand_total: payloadListPromotion.grand_total || 0,
      },
    };

    try {
      await validateVoucher.mutateAsync({
        body: payload,
        token: token as string,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        const rawMessage = error.response?.data?.data || error.response?.data?.message;
        setErrorVoucher(parseErrorMessage(rawMessage));
      }
    }
  };

  const isAllProductInclude = (promo: BasePromotion) => {
    const include = promo.product_reward?.find(
      (r) => r.reward_type === 'product' && r.rule_type === 'include'
    );
    if (!include) return false;

    const items = include.reward_items ?? [];
    if (items.length !== 1) return false;

    const it = items[0];
    const name = String(it?.item_name ?? '');
    const type = String(it?.item_type ?? '').toLowerCase();

    const ALL_PATTERNS = [/^\s*all\s*product[s]?\s*$/i, /^\s*semua\s*produk\s*$/i];

    const looksAllByName = ALL_PATTERNS.some((rx) => rx.test(name));
    const looksAllByType = type === 'all' || type === 'all_product';

    return looksAllByName || looksAllByType;
  };

  const isFixedSingleGift = (promo: BasePromotion) => {
    const include = promo.product_reward?.find(
      (r) => r.reward_type === 'product' && r.rule_type === 'include'
    );
    if (!include) return false;

    const items = include.reward_items ?? [];
    if (items.length !== 1) return false;

    return !isAllProductInclude(promo);
  };

  const isChoiceGiftPromo = (p: BasePromotion) =>
    !isFixedSingleGift(p) &&
    (p.product_reward?.some((r) => r.reward_type === 'product' && r.rule_type === 'include') ??
      false);

  const getMaxQtyReward = (promo: BasePromotion) => {
    const include = promo.product_reward?.find(
      (r) => r.reward_type === 'product' && r.rule_type === 'include'
    );
    const base = Math.max(0, include?.qty ?? 0);
    const counted = promo.counted_multiply ?? 0;
    return counted > 0 ? base * counted : base;
  };

  /**
   * Handles toggling promotion
   * @param {BasePromotion} promotion - Promotion object
   * @returns {void}
   */
  const handleSelectPromo = (promotion: BasePromotion): void => {
    const isSelected = selectedPromotion?.some((p) => p.promotion_id === promotion?.promotion_id);

    if (isSelected) {
      setSelectedPromotion(
        selectedPromotion?.filter((p) => p.promotion_id !== promotion.promotion_id)
      );
      replaceFreeGift(promotion.promotion_id, []);
      return;
    }

    const hasProductReward = (promotion.product_reward?.length ?? 0) > 0;

    if (hasProductReward) {
      if (isAllProductInclude(promotion)) {
        setPendingPromotion(promotion);
        setGiftPromotion(promotion);
        setMaxQtyReward(getMaxQtyReward(promotion));
        modalGift.setIsShow(true);
        return;
      }

      if (isFixedSingleGift(promotion)) {
        const groups = buildAutoFreeGifts(promotion);
        if (groups.length > 0) {
          replaceFreeGift?.(promotion.promotion_id, groups);
          setSelectedPromotion([...(selectedPromotion ?? []), promotion]);

          setPendingPromotion(null);
          setGiftPromotion(promotion);
          setMaxQtyReward(getMaxQtyReward(promotion));
          return;
        }
      }

      setPendingPromotion(promotion);
      setGiftPromotion(promotion);
      setMaxQtyReward(getMaxQtyReward(promotion));
      modalGift.setIsShow(true);
      return;
    }

    setSelectedPromotion([...(selectedPromotion ?? []), promotion]);
  };

  const { mutateAsync: mutateApplyPromotion, isPending: isLoadingApplyPromotion } =
    useMutateApplyPromotion({
      onSuccess: async (data) => {
        if (data.statusCode === 200) {
          await queryClient.invalidateQueries({ queryKey: ['checkoutDetail', checkoutId] });
          setSectionView('detail');
        }
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data.data || error.response?.data?.message;
          toast({
            title: 'Error',
            description: parseErrorMessage(errorMessage) || 'Something went wrong',
            variant: 'destructive',
          });
        }
      },
    });

  const promoVoucherCodeMap = (carts.promotions ?? []).reduce(
    (acc: Record<string, string>, promo) => {
      if (promo.promotion_id && promo.voucher_code) {
        acc[promo.promotion_id] = promo.voucher_code;
      }
      return acc;
    },
    {}
  );

  const selectedPromotionIds = selectedPromotion?.map((promo) => promo.promotion_id) ?? [];
  const selectedFreeGiftIds = selectedFreeGift?.map((gift) => gift.promotion_id) || [];

  const promotionsFromCartItems = (carts.items ?? [])
    .filter(
      (item) =>
        item.promotion_id &&
        selectedPromotionIds.includes(item.promotion_id) &&
        !selectedFreeGiftIds.includes(item.promotion_id)
    )
    .map((item) => {
      const base = {
        promotion_id: item.promotion_id as string,
        items: [
          {
            item_id: item.id,
            qty: item.quantity,
          },
        ],
      };
      // if the item has a voucher code, add it to the promotion payload
      const voucher_code = promoVoucherCodeMap[item.promotion_id as string];
      return voucher_code ? { ...base, voucher_code } : base;
    });

  const allPromotionsMap = useMemo(() => {
    const map = new Map<string, BasePromotion>();
    allPromotions?.forEach((promo) => map.set(promo.promotion_id, promo));
    return map;
  }, [allPromotions]);

  const normalizedSelectedPromotion = useMemo(() => {
    return (selectedPromotion ?? []).map((promo) => {
      const fullPromo = allPromotionsMap.get(promo.promotion_id);
      if (fullPromo) {
        return {
          ...fullPromo,
          voucher_code: promo.voucher_code || fullPromo.voucher_code,
        };
      }
      return promo;
    });
  }, [selectedPromotion, allPromotionsMap]);

  const generatedPromotions = generatePromotionsPayload(
    normalizedSelectedPromotion,
    selectedFreeGift
  );
  const finalPromotionsPayload = mergePromotions([
    ...generatedPromotions,
    ...promotionsFromCartItems,
  ]);

  /**
   * Handle use voucher
   * This function will send a request to the server to apply the promotions
   * and update the state of selectedPromotion and selectedFreeGift.
   * @returns - Void
   */
  const handleUseVoucher = async () => {
    await mutateApplyPromotion({
      body: {
        cart_id: checkoutId as string,
        promotions: finalPromotionsPayload,
      },
      token: token as string,
    });
  };

  /**
   * Handle confirm replace voucher
   * When user confirm to replace current voucher with the new one,
   * this function will update the state of selectedPromotion and
   * pendingReplacePromos, and close the modal use voucher. After that,
   * it will redirect to detail section.
   * @returns - Void
   */
  const handleConfirmReplace = () => {
    if (!pendingReplacePromos?.length) return;

    const rewardsWithCode = pendingReplacePromos?.map((promo) => ({
      ...promo,
      voucher_code: voucherCode,
    }));

    const prevIds = new Set((selectedPromotion ?? []).map((p) => p.promotion_id));

    const isNewOverlapFalse = rewardsWithCode.some((p) => p.is_overlap === false);
    applyVoucherRewards(rewardsWithCode, voucherCode, isNewOverlapFalse);

    const nextSelected = rewardsWithCode;
    const nextIds = new Set(nextSelected?.map((p) => p.promotion_id));

    prevIds.forEach((id) => {
      if (!nextIds.has(id)) removeFreeGift(id);
    });

    const singleGiftPromos = rewardsWithCode.filter(
      (p) =>
        (p.product_reward?.some((r) => r.reward_type === 'product' && r.rule_type === 'include') ??
          false) &&
        isFixedSingleGift(p)
    );
    const needChoicePromos = rewardsWithCode.filter(
      (p) =>
        (p.product_reward?.some((r) => r.reward_type === 'product' && r.rule_type === 'include') ??
          false) &&
        !isFixedSingleGift(p)
    );

    const sources = [...rewardsWithCode, ...(listPromotions ?? [])];

    for (const sp of singleGiftPromos) {
      if (!nextIds.has(sp.promotion_id)) continue; // kalau akhirnya tidak aktif, skip
      const enriched = ensureApplied(sp, sources);
      const gifts = buildAutoFreeGifts(enriched);
      if (gifts.length > 0) replaceFreeGift(sp.promotion_id, gifts, 'replace');
      else removeFreeGift(sp.promotion_id);
    }
    syncFreeGiftPromotion();

    if (needChoicePromos.length > 0) {
      const p0 = needChoicePromos[0];
      setPendingPromotion(p0);
      setGiftPromotion(p0);
      setMaxQtyReward(
        p0.counted_multiply
          ? (p0.product_reward?.[0]?.qty ?? 0) * p0.counted_multiply
          : p0.product_reward?.[0]?.qty ?? 0
      );
      modalUseVoucher.setIsShow(false);
      modalGift.setIsShow(true);
    } else {
      // tidak butuh pilihan lagi — tutup modal replace
      setPendingReplacePromos(null);
      modalUseVoucher.setIsShow(false);
    }
  };

  const handleSaveFreeGift = (data: BaseFreeGift[]) => {
    if (!pendingPromotion) return;

    const isDuplicate = selectedPromotion?.some(
      (promo) => promo.promotion_id === pendingPromotion.promotion_id
    );
    const hasOverlapPromo = selectedPromotion?.some((p) => p.is_overlap === true);

    if (isDuplicate) {
      toast({
        variant: 'destructive',
        title: 'Error!',
        description: 'This promotion is already selected',
      });
    }

    if (!pendingPromotion.is_overlap) {
      setSelectedPromotion([pendingPromotion]);
      replaceFreeGift(String(giftPromotion?.promotion_id), data);
      return;
    }

    if (hasOverlapPromo) {
      setSelectedPromotion([...(selectedPromotion ?? []), pendingPromotion]);
    } else {
      setSelectedPromotion([pendingPromotion]);
    }

    replaceFreeGift(String(giftPromotion?.promotion_id), data);
  };

  useEffect(() => {
    const listPromotions = carts?.promotions || [];
    const redeemedVouchers = listPromotions.filter((promo) => promo.use_voucher);

    if (sectionView === 'voucher' && listPromotions.length > 0) {
      setSelectedPromotion(listPromotions);
      setRedeemedVouchers(redeemedVouchers);
      setPendingReplacePromos(null);
      setPendingPromotion(null);
      setGiftPromotion(null);
      setMaxQtyReward(0);
    }
  }, [sectionView, carts?.promotions?.length]);

  useEffect(() => {
    if (!modalUseVoucher.isShow) {
      setPendingReplacePromos(null);
    }
  }, [modalUseVoucher.isShow]);

  useEffect(() => {
    if (errorVoucher) {
      const timeout = setTimeout(() => {
        setErrorVoucher(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [errorVoucher]);

  return (
    <>
      <div className='flex items-center gap-x-2 pb-6'>
        <ChevronLeft className='cursor-pointer' onClick={() => setSectionView('detail')} />
        <h2 className='text-xl'>
          Select voucher{' '}
          {(selectedPromotion?.length ?? 0) > 0 && `(${selectedPromotion?.length} selected)`}
        </h2>
      </div>

      <div
        className='py-2 lg:px-4'
        style={
          {
            '--text-primary': promotionTheme?.colorScheme?.text || '#000000',
            '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#000000',
            '--background': promotionTheme?.colorScheme?.background || '#FFFFFF',
            '--btn-label': promotionTheme?.colorScheme?.btnSolidlabel || '#FFFFFF',
            '--btn-background': promotionTheme?.colorScheme?.btnSolidBackgroud || '#000000',
          } as React.CSSProperties
        }
      >
        <div className='flex items-center gap-2'>
          <input
            type='text'
            placeholder='Enter voucher code'
            className={cn(
              'w-full flex-1 rounded px-3 py-2.5 text-xs outline-none ring-1 focus:ring-1',
              errorVoucher ? 'ring-red-500' : 'ring-[var(--background)]'
            )}
            value={voucherCode}
            onChange={(e) => setVoucherCode(e.target.value)}
          />

          <Button
            type='button'
            size='sm'
            variant={isEmpty(colorScheme) ? 'secondary' : null}
            className={cn(
              'items-center rounded-sm py-5 text-xs font-normal transition-all duration-300 hover:opacity-80',
              !isEmpty(colorScheme) && 'bg-[var(--btn-background)] text-[var(--btn-label)]'
            )}
            onClick={handleRedeemVoucher}
            disabled={validateVoucher.isPending}
          >
            {validateVoucher.isPending && <LoadingAnimationIcon size={4} />} Redeem
          </Button>
        </div>
        {errorVoucher && <p className='mt-1 text-xs text-red-500'>{errorVoucher}</p>}
      </div>

      <ScrollArea className='max-h-[600px] overflow-y-auto pt-4 lg:px-4' type='scroll'>
        {isLoadingPromotions ? (
          <div className='flex flex-col gap-1.5'>
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton key={index} className='h-20 w-full' />
            ))}
          </div>
        ) : (
          <div className='flex flex-col gap-1.5'>
            {allPromotions && allPromotions?.length > 0 ? (
              allPromotions?.map((promotion) => {
                const isSelected = selectedPromotion.some(
                  (p) => p.promotion_id === promotion.promotion_id
                );
                const percentage = Math.min(
                  (payloadListPromotion.grand_total / promotion.minimum_spending) * 100,
                  100
                );
                const isEligiblePromo =
                  (payloadListPromotion.grand_total || 0) >= promotion.minimum_spending;
                const isDisabled = isPromoDisabled(promotion);

                return (
                  <CardPromotion
                    key={promotion.promotion_id}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    percentage={percentage}
                    promotion={promotion}
                    isEligiblePromo={isEligiblePromo}
                    grandTotal={payloadListPromotion.grand_total || 0}
                    handleSelectPromo={handleSelectPromo}
                    colorScheme={colorScheme}
                    theme={promotionTheme}
                  />
                );
              })
            ) : (
              <p className='text-center text-sm text-gray-500'>No promotions available</p>
            )}
          </div>
        )}
      </ScrollArea>

      <div className='mt-4 flex flex-col gap-2 lg:px-4'>
        {selectedFreeGift.length > 0 && (
          <>
            <h3 className='text-sm font-semibold'>Your Free Gift</h3>
            <div className='max-h-[200px] overflow-y-auto'>
              {!selectedFreeGift.length ? (
                <p className='text-sm text-gray-500'>No free gift selected</p>
              ) : (
                selectedFreeGift.map((gift) => (
                  <div key={gift.promotion_id} className='flex items-center justify-between py-2'>
                    {gift.items.map((item) => (
                      <div key={item.item_id} className='flex flex-row items-center gap-2'>
                        <DynamicImage
                          src={
                            (item.product_assets?.[0] as string) ||
                            String(gift.product_group.product_assets?.[0])
                          }
                          alt={item.item_name as string}
                          width={40}
                          height={40}
                          className='aspect-square h-10 w-10 rounded border object-contain'
                        />
                        <div className='min-w-0'>
                          <p
                            className='line-clamp-1 text-sm font-medium leading-tight'
                            title={`${gift.product_group.product_name} ${
                              item.is_variants ? `- ${item.item_name}` : ''
                            }`}
                          >
                            {gift.product_group.product_name}{' '}
                            {item.is_variants ? `- ${item.item_name}` : ''}
                          </p>
                          <p className='text-xs text-gray-500'>Qty: {item.qty}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </>
        )}

        <div
          style={
            {
              '--text-primary': promotionTheme?.colorScheme?.text || '#000000',
              '--text-secondary': promotionTheme?.colorScheme?.textSecondary || '#000000',
              '--background': promotionTheme?.colorScheme?.background || '#FFFFFF',
              '--btn-label': promotionTheme?.colorScheme?.btnSolidlabel || '#FFFFFF',
              '--btn-background': promotionTheme?.colorScheme?.btnSolidBackgroud || '#000000',
            } as React.CSSProperties
          }
        >
          <Button
            type='button'
            variant={isEmpty(colorScheme) ? 'default' : null}
            onClick={() => modalUseVoucher.open()}
            disabled={isLoadingPromotions || isLoadingApplyPromotion}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm py-5 transition-all duration-300 hover:opacity-80',
              !isEmpty(colorScheme) && 'bg-[var(--btn-background)] text-[var(--btn-label)]'
            )}
          >
            {(isLoadingPromotions || isLoadingApplyPromotion) && (
              <LoadingAnimationIcon size={4} color='white' />
            )}
            Use voucher
          </Button>
        </div>
      </div>

      <ModalGiftReward
        openModal={modalGift.isShow}
        setOpenModal={modalGift.setIsShow}
        promotion={giftPromotion as BasePromotion}
        pendingPromotion={pendingPromotion as BasePromotion}
        setPromotion={setGiftPromotion}
        setSelectedPromotion={setSelectedPromotion}
        token={token}
        onSaveGift={handleSaveFreeGift}
        maxQty={maxQtyReward}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        colorScheme={promotionTheme?.colorScheme}
      />

      <ModalAlert
        openModal={modalUseVoucher.isShow}
        setOpenModal={modalUseVoucher.setIsShow}
        title='Are you sure'
        description={
          pendingReplacePromos
            ? 'This will replace your currently selected promotions. Continue?'
            : 'This action will overwrite your previously active promotions'
        }
        onConfirm={pendingReplacePromos ? handleConfirmReplace : handleUseVoucher}
      />
    </>
  );
}
